import { z } from 'zod';
import { ConfirmationOutcome, Priority, RequestAction, RequestStatus, SourceChannel, Visibility } from './enums.js';
import {
  ContentLocale,
  IsoDateTime,
  LongMarkdown,
  PersonList,
  Reason,
  RequestKey,
  Title,
  UserRef,
  SortDirection,
  Uuid,
  UuidList,
  Version,
  boundedList,
} from './common.js';
import { AccessGrantInput } from './identity.js';
import { ProjectRef, TeamRef } from './work.js';

/** Functional progress a requester may see without access to execution tasks. */
export const FulfillmentSummary = z.object({
  stage: z.enum(['NOT_STARTED', 'IN_PROGRESS', 'IN_REVIEW', 'AWAITING_DEPLOYMENT', 'AWAITING_ACTIVATION', 'DELIVERED', 'NONE_LINKED']),
  requiredLinkedVisibleCount: z.number().int(),
  hasHiddenWork: z.boolean(),
  directDeliverable: z.boolean(),
});
export type FulfillmentSummary = z.infer<typeof FulfillmentSummary>;

export const ConfirmationView = z.object({
  generation: z.number().int(),
  outcome: ConfirmationOutcome,
  commentMarkdown: z.string().nullable(),
  respondedAt: IsoDateTime.nullable(),
  respondedBy: UserRef.nullable(),
});
export type ConfirmationView = z.infer<typeof ConfirmationView>;

export const RequestView = z.object({
  id: Uuid,
  key: RequestKey,
  aliases: z.array(z.string()),
  version: Version,
  contentRevision: z.number().int(),
  title: z.string(),
  problemMarkdown: z.string(),
  currentBehavior: z.string().nullable(),
  expectedOutcome: z.string().nullable(),
  impact: z.string().nullable(),
  impactUnknown: z.boolean(),
  reproduction: z.string().nullable(),
  affectedArea: z.string().nullable(),
  status: RequestStatus,
  priority: Priority,
  visibility: Visibility,
  author: UserRef.nullable(),
  assignee: UserRef.nullable(),
  triageTeam: TeamRef.nullable(),
  project: ProjectRef.nullable(),
  sourceChannel: SourceChannel,
  sourceReference: z.string().nullable(),
  originalLocale: z.string(),
  fulfillmentGeneration: z.number().int(),
  unresolvedFeedback: z.boolean(),
  currentConfirmation: ConfirmationView.nullable(),
  fulfillment: FulfillmentSummary,
  duplicateOf: z.object({ id: Uuid, key: z.string(), title: z.string(), status: RequestStatus }).nullable(),
  pendingQuestion: z.string().nullable(),
  /** Section 9.3: request-level activation (for example a feature flag) observed by a person. */
  activation: z.object({
    required: z.boolean(),
    note: z.string().nullable(),
    activatedAt: IsoDateTime.nullable(),
    activatedBy: UserRef.nullable(),
    evidenceMarkdown: z.string().nullable(),
  }),
  decisionReason: z.string().nullable(),
  /** Hidden from the triage inbox until this time (then it returns on its own). */
  snoozedUntil: IsoDateTime.nullable(),
  submittedAt: IsoDateTime.nullable(),
  resolvedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  commentCount: z.number().int(),
  following: z.boolean(),
  availableActions: z.array(z.string()),
  canonicalUrl: z.string(),
});
export type RequestView = z.infer<typeof RequestView>;

export const RequestListItem = RequestView.pick({
  id: true,
  key: true,
  version: true,
  title: true,
  status: true,
  priority: true,
  visibility: true,
  author: true,
  assignee: true,
  triageTeam: true,
  project: true,
  sourceChannel: true,
  originalLocale: true,
  unresolvedFeedback: true,
  snoozedUntil: true,
  submittedAt: true,
  createdAt: true,
  updatedAt: true,
  commentCount: true,
});
export type RequestListItem = z.infer<typeof RequestListItem>;

/** Fields shared by draft creation and update. Unknown impact is valid and explicit. */
export const RequestDraftFields = z
  .object({
    title: Title.optional(),
    problemMarkdown: LongMarkdown.optional(),
    currentBehavior: z.string().max(8000).optional(),
    expectedOutcome: z.string().max(8000).optional(),
    impact: z.string().max(4000).optional(),
    impactUnknown: z.boolean().optional(),
    reproduction: z.string().max(8000).optional(),
    affectedArea: z.string().max(200).optional(),
    evidenceIds: boundedList(Uuid).optional(),
    originalLocale: ContentLocale.optional(),
    visibility: Visibility.optional(),
    grants: boundedList(AccessGrantInput).optional(),
    priority: Priority.optional(),
    triageTeamId: Uuid.optional(),
    projectId: Uuid.optional(),
  })
  .strict();
export type RequestDraftFields = z.infer<typeof RequestDraftFields>;

export const PrepareRequest = RequestDraftFields.extend({
  draftId: Uuid.optional(),
  expectedVersion: Version.optional(),
  sourceChannel: SourceChannel.optional(),
  sourceReference: z.string().max(500).optional(),
}).strict();
export type PrepareRequest = z.infer<typeof PrepareRequest>;

export const DuplicateCandidate = z.object({ id: Uuid, key: z.string(), title: z.string(), status: RequestStatus, reason: z.string() });
export type DuplicateCandidate = z.infer<typeof DuplicateCandidate>;

export const PrepareRequestResult = z.object({
  draft: RequestView,
  missingFields: z.array(z.string()),
  duplicateCandidates: z.array(DuplicateCandidate),
});
export type PrepareRequestResult = z.infer<typeof PrepareRequestResult>;

export const SubmitRequest = z.object({ draftId: Uuid, expectedVersion: Version }).strict();
export type SubmitRequest = z.infer<typeof SubmitRequest>;

export const UpdateRequest = z
  .object({
    expectedVersion: Version,
    title: Title.optional(),
    problemMarkdown: LongMarkdown.optional(),
    currentBehavior: z.string().max(8000).nullable().optional(),
    expectedOutcome: z.string().max(8000).nullable().optional(),
    impact: z.string().max(4000).nullable().optional(),
    impactUnknown: z.boolean().optional(),
    reproduction: z.string().max(8000).nullable().optional(),
    affectedArea: z.string().max(200).nullable().optional(),
    priority: Priority.optional(),
    assigneeId: Uuid.nullable().optional(),
    triageTeamId: Uuid.nullable().optional(),
    projectId: Uuid.nullable().optional(),
    /** Corrects a wrong language detection; translations are keyed by source locale and recreate. */
    originalLocale: ContentLocale.optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 1, { message: 'at least one editable field is required' });
export type UpdateRequest = z.infer<typeof UpdateRequest>;

export const DirectFulfillment = z
  .object({ outcomeMarkdown: LongMarkdown, evidenceIds: boundedList(Uuid).optional() })
  .strict();

export const TransitionRequest = z
  .object({
    expectedVersion: Version,
    action: RequestAction,
    reason: Reason.optional(),
    requestedInformation: z.string().min(1).max(4000).optional(),
    answerMarkdown: z.string().min(1).max(8000).optional(),
    canonicalRequestId: Uuid.optional(),
    directFulfillment: DirectFulfillment.optional(),
    correctionWorkItemId: Uuid.optional(),
  })
  .strict();
export type TransitionRequest = z.infer<typeof TransitionRequest>;

export const ConfirmRequest = z
  .object({
    expectedVersion: Version,
    fulfillmentGeneration: z.number().int().min(1),
    outcome: z.enum(['CONFIRMED', 'PERSISTS']),
    commentMarkdown: z.string().max(8000).optional(),
  })
  .strict();
export type ConfirmRequest = z.infer<typeof ConfirmRequest>;

export const LinkRequestWork = z
  .object({ expectedVersion: Version, workItemId: Uuid, required: z.boolean() })
  .strict();
export type LinkRequestWork = z.infer<typeof LinkRequestWork>;

export const REQUEST_SORTS = ['updatedAt', 'createdAt', 'priority', 'key', 'title', 'status', 'assignee', 'author'] as const;
export type RequestSort = (typeof REQUEST_SORTS)[number];

export const RequestListQuery = z.object({
  q: z.string().max(200).optional(),
  status: z.string().max(300).optional(),
  priority: z.string().max(100).optional(),
  /** People: ids, me or none, comma-separated (any of them). */
  assigneeId: PersonList.optional(),
  authorId: PersonList.optional(),
  triageTeamId: UuidList.optional(),
  projectId: UuidList.optional(),
  view: z.enum(['inbox', 'mine', 'all', 'following', 'attention', 'snoozed']).optional(),
  sort: z.enum(REQUEST_SORTS).optional(),
  dir: SortDirection.optional(),
  cursor: z.string().max(512).optional(),
  limit: z.string().regex(/^\d{1,3}$/).optional(),
});
export type RequestListQuery = z.infer<typeof RequestListQuery>;

/** Snooze a request out of the triage inbox until a time in the future; null wakes it now. */
export const SnoozeRequest = z.object({ expectedVersion: Version, until: IsoDateTime.nullable() }).strict();
export type SnoozeRequest = z.infer<typeof SnoozeRequest>;

export const RequestDecisionView = z.object({
  id: Uuid,
  action: z.string(),
  fromStatus: RequestStatus,
  toStatus: RequestStatus,
  reason: z.string().nullable(),
  actor: UserRef.nullable(),
  occurredAt: IsoDateTime,
});
export type RequestDecisionView = z.infer<typeof RequestDecisionView>;

/** Triagers require or waive activation; an observation records who saw it live and the evidence. */
export const SetRequestActivation = z.discriminatedUnion('action', [
  z.object({ action: z.literal('REQUIRE'), expectedVersion: Version, note: z.string().min(1).max(1000) }).strict(),
  z.object({ action: z.literal('WAIVE'), expectedVersion: Version, reason: z.string().min(1).max(500) }).strict(),
  z.object({ action: z.literal('OBSERVE'), expectedVersion: Version, evidenceMarkdown: z.string().min(1).max(8000) }).strict(),
]);
export type SetRequestActivation = z.infer<typeof SetRequestActivation>;

/** Section 4.4: moving a request into a project changes who can see it; preview before applying. */
export const ProjectMovePreview = z.object({
  restrictedBefore: z.boolean(),
  restrictedAfter: z.boolean(),
  /** People involved (author, assignee, followers) who would no longer be able to read it. */
  losesAccess: z.array(UserRef),
  assigneeLosesAccess: z.boolean(),
});
export type ProjectMovePreview = z.infer<typeof ProjectMovePreview>;
